<script setup lang="ts">
import { Comark } from '@comark/vue'

definePageMeta({ layout: 'text-content-page' })

const { locale, t } = useI18n({
  useScope: 'local'
})

useHead({
  title: t('title'),
  htmlAttrs: {
    lang: locale.value
  },
  meta: [
    {
      name: 'description',
      content: t('description')
    }
  ]
})

const md_de = `# Haltestellendaten

Für Bahn-Vorhersage mussten wir Informationen über Haltestellen sammeln. Wir hatten mehrere Anforderungen an die Daten, die von keinem einzelnen bereits verfügbaren Datensatz erfüllt wurden:

- vollständige Abdeckung der DB Timetables-API Haltestellen
- Geokoordinaten
- Archiv (stillgelegte oder umbenannte Haltestellen)

Wir aktualisieren den Haltestellendatensatz regelmäßig. Wir durchsuchen unsere Verspätungsdaten nach referenzierten Haltestellen, die wir noch nicht kennen und sammeln sie aus verschiedenen Datenquellen in der folgenden Reihenfolge:

1. [DB RIS::Stations API](https://developers.deutschebahn.com/db-api-marketplace/apis/product/ris-stations){target="_blank"}
2. HAFAS, unter Verwendung des [db-vendo-client](https://github.com/public-transport/db-vendo-client){target="_blank"}
3. Die Listen von Stationen, die für [Travel-Status-DE-IRIS](https://github.com/derf/Travel-Status-DE-IRIS){target="_blank"} verwendet werden
4. [Trainlines Stationsdatenbank](https://github.com/trainline-eu/stations){target="_blank"}
5. Das deutsche [Zentrale Haltestellenverzeichnis (ZHV)](https://zhv.wvigmbh.de/){target="_blank"} von DELFI
6. Manuelle Eingaben

Wir erwarten, dass der Datensatz unvollständig ist und einige fehlerhafte Daten enthält, aber dafür passt er zu unseren Zugverspätungsdaten und enthält eine Historie von Änderungen.

## Daten

Die Daten sind in der Mobilithek verfügbar:

::btn-link{href="https://mobilithek.info/offers/940263406913355776"}
<i class="icon icon-box-arrow-up-right"></i> Haltestellendaten herunterladen
::

Der Datensatz enthält zwei Dateien im Parquet-Format: \`stations.parquet\` und \`stops.parquet\`. Der vollständige Datensatz ist in \`stations.parquet\` enthalten. \`stops.parquet\` ist eine GTFS \`stops.txt\` ähnliche Datei, die vom geparsten Zugverspätungsdatensatz referenziert wird.

## Lizenz

Die Daten sind unter der [Open Database License](https://opendatacommons.org/licenses/odbl/){target="_blank"} verfügbar. Credits müssen an Bahn-Vorhersage, Deutsche Bahn, Trainline und DELFI zugeordnet werden.

_Wenn Du ein Problem mit dieser Dokumentation findest, dann öffne doch bitte ein Issue auf [GitLab](https://gitlab.com/bahnvorhersage/bahnvorhersage_frontend){target="_blank"}._
`

const md_en = `# Stop Data

For Bahn-Vorhersage we needed to collect information about train stops. We had multiple requirements for the data that were not met by any single available dataset:

- full coverage of DB Timetables API stops
- geo coordinates
- archive (abandoned or renamed stops)

We regularly update the stop dataset. We search our delay data for referenced stops that we do not yet have and collect them from different data sources in the following order:

1. [DB RIS::Stations API](https://developers.deutschebahn.com/db-api-marketplace/apis/product/ris-stations){target="_blank"}
2. HAFAS, by using the [db-vendo-client](https://github.com/public-transport/db-vendo-client){target="_blank"}
3. The lists of stations used for [Travel-Status-DE-IRIS](https://github.com/derf/Travel-Status-DE-IRIS){target="_blank"}
4. [Trainlines station database](https://github.com/trainline-eu/stations){target="_blank"}
5. The German [Zentrales Haltestellenverzeichnis (ZHV)](https://zhv.wvigmbh.de/){target="_blank"} from DELFI
6. Manual effort

We expect the dataset to be incomplete and to contain some incorrect data, but it matches our train delay data and contains a history of changes.

## Data

The data is available in the Mobilithek:

::btn-link{href="https://mobilithek.info/offers/940263406913355776"}
<i class="icon icon-box-arrow-up-right"></i> Download stop Data
::

The dataset contains two files in Parquet format: \`stations.parquet\` and \`stops.parquet\`. The full dataset is contained in \`stations.parquet\`. Use \`stops.parquet\` only together with the train delay data, it is structured similarly to the GTFS \`stops.txt\` file.

## License

The data is available under the [Open Database License](https://opendatacommons.org/licenses/odbl/){target="_blank"}. Credits must be attributed to Bahn-Vorhersage, Deutsche Bahn, Trainline and DELFI.

_If you find any issue with this documentation, please consider contributing on [GitLab](https://gitlab.com/bahnvorhersage/bahnvorhersage_frontend){target="_blank"}._
`

const md = computed(() => (locale.value === 'de' ? md_de : md_en))
</script>

<template>
  <LocaleSwitcher />
  <Suspense>
    <Comark>{{ md }}</Comark>
  </Suspense>
</template>

<i18n lang="yaml">
de:
  title: Haltestellendaten
  description: Daten über Bahnhöfe..
en:
  title: Stop Data
  description: Data about train stations.
</i18n>
